import { Box } from '@mui/material';
import {
  useCallback, useEffect, useReducer, useRef,
} from 'react';
import ImageSettings, { Size } from '../../../../models/BlockComponentSettings/ImageSettings';
import BlockComponentImage from '../../../../models/BlockComponentValues/BlockComponentImage';

interface ImagePreviewProps {
  image: BlockComponentImage;
  crop: Size;
  handleChange: (settings: ImageSettings) => void;
  handleImageSizeChange: (size: Size) => void;
}

const ImagePreview = ({
  image, crop, handleChange, handleImageSizeChange,
}: ImagePreviewProps) => {
  const positionRef = useRef(image.settings.position);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const handleCrop = useCallback(() => {
    handleChange({
      ...image.settings,
      position: positionRef.current,
      size: {
        width: containerRef.current?.clientWidth,
        height: containerRef.current?.clientHeight,
      },
      frameSize: { width: crop.width, height: crop.height },
    });
  }, [handleChange, image.settings, crop]);

  const handleMouseMove = (event: MouseEvent) => {
    event.preventDefault();
    positionRef.current = {
      x: positionRef.current.x + event.movementX,
      y: positionRef.current.y + event.movementY,
    };
    forceUpdate();
  };
  const handleMouseUp = (event: MouseEvent) => {
    event.preventDefault();
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    handleCrop();
  };

  useEffect(() => {
    handleCrop();
  }, [handleCrop]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '605px',
        height: '340px',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'move',
      }}
      onMouseDown={(event) => {
        event.preventDefault();
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      }}
    >
      <img
        id="image-preview"
        ref={imageRef}
        src={image.value}
        alt="being edited"
        role="presentation"
        style={{
          objectFit: 'contain',
          width: '100%',
          height: '100%',
          transform: `translate(${positionRef.current.x}px, ${positionRef.current.y}px) scale(${image.settings.scale})`,
        }}
        onLoad={() => {
          handleImageSizeChange({ width: imageRef.current?.naturalWidth, height: imageRef.current?.naturalHeight });
        }}
      />
      <Box
        id="crop-box"
        sx={{
          position: 'absolute',
          width: crop.width,
          height: crop.height,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, .5)',
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
};

export default ImagePreview;
